/**
 * Small presentational building blocks for the Developer Help recipe pages:
 * a paper Section card, a copyable CodeBlock, and a tabbed multi-language
 * code sample. Follows the visual patterns of TranscriptionHowToUsePage.
 */

import { useState } from 'react';
import { Tabs, Tab } from '@mui/material';
import { Copy as CopyIcon } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { useNotificationStore } from '../../store/notificationStore';
import { RECIPE_LANGUAGE_LABELS, type RecipeCodeSample, type RecipeLanguage } from '../../content/developerHelp/types';

export const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border border-border bg-card p-3 mb-3">
    <h6 className="text-base font-medium mb-2">{title}</h6>
    {children}
  </div>
);

export const CodeBlock = ({ code }: { code: string }) => {
  const { addNotification } = useNotificationStore();

  const copy = async () => {
    await navigator.clipboard.writeText(code);
    addNotification('Example copied to clipboard', 'success');
  };

  return (
    <div className="relative">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={copy}
              className="absolute top-2 right-2 z-10 h-8 w-8"
            >
              <CopyIcon size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <pre className="p-2 m-0 overflow-auto border border-border rounded bg-accent/10 font-mono text-[13px] leading-relaxed">
        {code}
      </pre>
    </div>
  );
};

/** Renders a code sample; shows language tabs only when more than one language exists. */
export const CodeSampleBlock = ({ sample }: { sample: RecipeCodeSample }) => {
  const languages = Object.keys(sample.languages) as RecipeLanguage[];
  const [active, setActive] = useState<RecipeLanguage>(languages[0]);
  const activeCode = sample.languages[active] ?? sample.languages[languages[0]] ?? '';

  return (
    <div className="mt-2">
      {sample.title && (
        <p className="text-sm text-muted-foreground mb-1">{sample.title}</p>
      )}
      {languages.length > 1 && (
        <Tabs value={active} onChange={(_, value: RecipeLanguage) => setActive(value)} sx={{ mb: 1, minHeight: 36 }}>
          {languages.map((lang) => (
            <Tab key={lang} value={lang} label={RECIPE_LANGUAGE_LABELS[lang]} sx={{ minHeight: 36, py: 0 }} />
          ))}
        </Tabs>
      )}
      <CodeBlock code={activeCode} />
    </div>
  );
};
